import { useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import {
  ArchiveBoxIcon,
  ClipboardDocumentListIcon,
  DocumentMagnifyingGlassIcon,
  ShieldExclamationIcon,
  BoltIcon,
  CubeTransparentIcon,
  ArrowLeftStartOnRectangleIcon,
  Bars3Icon,
  CalendarDaysIcon,
  ChartBarIcon,
  ClipboardDocumentCheckIcon,
  MapPinIcon,
  ClockIcon,
  ComputerDesktopIcon,
  CubeIcon,
  ShoppingCartIcon,
  DevicePhoneMobileIcon,
  InboxArrowDownIcon,
  KeyIcon,
  LifebuoyIcon,
  SparklesIcon,
  MagnifyingGlassIcon,
  PaperAirplaneIcon,
  ShieldCheckIcon,
  TruckIcon,
  Bars3BottomLeftIcon,
  UserGroupIcon,
  UserIcon,
  UserMinusIcon,
  WrenchScrewdriverIcon,
  XMarkIcon,
  ScaleIcon,
  ArrowsRightLeftIcon,
  TrophyIcon,
} from '@heroicons/react/24/outline';
import {
  ALWAYS_VISIBLE_LINKS,
  ALWAYS_VISIBLE_ROUTES,
  DEPARTMENT_SPINE,
  PLACEHOLDER_ITEMS,
  PLATFORM_ITEMS,
  filterDepartmentsByAccess,
  isFullAdmin,
} from '../nav/navManifest';
import { useInboxUnread } from '../hooks/useComms';

// Re-exported so callers keep importing the nav from the component that renders
// it, rather than every one of them learning where the manifest lives.
export { filterDepartmentsByAccess, isFullAdmin };

// The per-department cards, keyed by the manifest's department key.
//
// These stay here rather than in navManifest.js because this is the only app
// that renders them: the other four rails deep-link to `/?dept=<key>` and never
// see an item. The manifest owns which departments exist and in what order;
// this owns what is inside one.
const DEPARTMENT_ITEMS = {
  it: [
    {
      name: 'Nano User Permissions',
      description: 'Control who has access to each Nano department and module.',
      icon: KeyIcon,
      url: process.env.REACT_APP_NEO2_PERMISSIONS_URL,
      group: 'Administration',
    },
    {
      name: 'BrightHR Leaver Check',
      description: 'Enabled Entra accounts not showing as active in BrightHR — possible missed leavers.',
      icon: UserGroupIcon,
      url: process.env.REACT_APP_LEAVER_CHECK_URL,
      group: 'Administration',
    },
    {
      name: 'Leaver Management',
      description: 'Offboard leavers — convert mailbox to shared, remove licences, block sign-in. Full-admin / IT Administration only.',
      icon: UserMinusIcon,
      url: process.env.REACT_APP_LEAVERS_URL,
      group: 'Administration',
      headerKey: 'itAdministration',
    },
  ],
  infosec: [
    {
      name: 'Statement of Applicability',
      description: 'All 93 Annex A controls — applicability, ownership, status, and evidence.',
      icon: ClipboardDocumentCheckIcon,
      url: process.env.REACT_APP_SOA_URL,
    },
    {
      name: 'ISO 27001 ISMS Tracker',
      description: 'Readiness dashboard, mandatory clauses 4–10, and audit register.',
      icon: ClipboardDocumentCheckIcon,
      url: process.env.REACT_APP_ISO_TRACKER_URL,
    },
    {
      name: '365 Access Review History',
      description: 'Weekly point-in-time snapshots of the 365 directory, kept for audit evidence.',
      icon: ClockIcon,
      url: process.env.REACT_APP_ACCESS_REVIEW_HISTORY_URL,
    },
  ],
  hr: [
    {
      name: 'HR Cases',
      description: 'Disciplinary, grievance and investigation cases, with the activity evidence behind them.',
      icon: ChartBarIcon,
      url: process.env.REACT_APP_HR_ACTIVITY_URL,
      // The department is the room; this is the locked drawer. Being in
      // NEO2-HR-Access shows the card, but opening it needs the separate
      // monitoring group — so nobody is shown a card that then 403s.
      headerKey: 'hrActivityMonitoring',
    },
    {
      name: 'Activity Monitoring',
      description: 'Pull and review employee activity, then attach an extract to a case as evidence.',
      icon: MagnifyingGlassIcon,
      url: process.env.REACT_APP_HR_MONITORING_URL,
      // The narrower of the two HR grants. HR Cases above is the department's;
      // this is the Head of HR's, and the card is hidden rather than shown and
      // refused.
      headerKey: 'hrActivityMonitoring',
    },
    {
      name: 'Absence & Late Reporting',
      description: 'See who has been reported late or absent today, or across any date range, and act on it.',
      icon: CalendarDaysIcon,
      // Internal route, not a cross-app link — no headerKey, so any HR
      // department member sees it (matches the FSM form's own access: every
      // engineer/manager with base FSM access, not a narrower group).
      url: '/absence-reporting',
    },
  ],
  // One card per nano-fibre-inspections route, grouped/gated to mirror that
  // app's own InspectionTypes.jsx dashboard exactly — Management gates the 4
  // forms, Administration gates re-inspections/dashboards/track changes, same
  // headerKey names as that repo's permissionGroups.js (kept in sync per the
  // note at the top of this app's own server/config/permissionGroups.js).
  'health-safety': [
    {
      name: 'Harness',
      description: 'View and manage harness inspection records.',
      icon: UserIcon,
      url: process.env.REACT_APP_INSPECTIONS_URL && `${process.env.REACT_APP_INSPECTIONS_URL}/harness/inspections`,
      group: 'Health & Safety Management',
      headerKey: 'healthSafetyManagement',
    },
    {
      name: 'Ladder',
      description: 'View and manage ladder inspection records.',
      icon: Bars3Icon,
      url: process.env.REACT_APP_INSPECTIONS_URL && `${process.env.REACT_APP_INSPECTIONS_URL}/ladder/inspections`,
      group: 'Health & Safety Management',
      headerKey: 'healthSafetyManagement',
    },
    {
      name: 'Pre-Climb Audit',
      description: 'View and manage pre-climb audit records.',
      icon: ShieldCheckIcon,
      url: process.env.REACT_APP_INSPECTIONS_URL && `${process.env.REACT_APP_INSPECTIONS_URL}/pre-climb/inspections`,
      group: 'Health & Safety Management',
      headerKey: 'healthSafetyManagement',
    },
    {
      name: 'Safe Drilling Audit',
      description: 'View and manage safe drilling audit records.',
      icon: WrenchScrewdriverIcon,
      url: process.env.REACT_APP_INSPECTIONS_URL && `${process.env.REACT_APP_INSPECTIONS_URL}/pre-drill/inspections`,
      group: 'Health & Safety Management',
      headerKey: 'healthSafetyManagement',
    },
    {
      name: 'Step Ladder',
      description: 'View and manage step ladder inspection records.',
      icon: ClipboardDocumentListIcon,
      url: process.env.REACT_APP_INSPECTIONS_URL && `${process.env.REACT_APP_INSPECTIONS_URL}/step-ladder/inspections`,
      group: 'Health & Safety Management',
      headerKey: 'healthSafetyManagement',
    },
    {
      name: 'Vehicle Inspection',
      description: 'View and manage vehicle inspection records.',
      icon: TruckIcon,
      url: process.env.REACT_APP_INSPECTIONS_URL && `${process.env.REACT_APP_INSPECTIONS_URL}/vehicle/inspections`,
      group: 'Health & Safety Management',
      headerKey: 'healthSafetyManagement',
    },
    {
      name: 'Retrospective Audit (SHEQ Only)',
      description: 'View and manage retrospective audit records.',
      icon: DocumentMagnifyingGlassIcon,
      url: process.env.REACT_APP_INSPECTIONS_URL && `${process.env.REACT_APP_INSPECTIONS_URL}/retrospective-audit/inspections`,
      group: 'Health & Safety Management',
      headerKey: 'healthSafetyManagement',
    },
    {
      name: 'PPE & Equipment Inspection (MEWP Only)',
      description: 'View and manage ppe & equipment inspection records.',
      icon: ShieldExclamationIcon,
      url: process.env.REACT_APP_INSPECTIONS_URL && `${process.env.REACT_APP_INSPECTIONS_URL}/ppe-equipment/inspections`,
      group: 'Health & Safety Management',
      headerKey: 'healthSafetyManagement',
    },
    {
      name: 'PPE & Equipment Inspection (SHEQ Only)',
      description: 'View and manage ppe & equipment inspection (SHEQ only) records.',
      icon: ClipboardDocumentCheckIcon,
      url: process.env.REACT_APP_INSPECTIONS_URL && `${process.env.REACT_APP_INSPECTIONS_URL}/ppe-equipment-sheq/inspections`,
      group: 'Health & Safety Management',
      headerKey: 'healthSafetyManagement',
    },
    {
      name: 'Warehouse Safety Checklist (SHEQ Only)',
      description: 'View and manage warehouse safety checklist records.',
      icon: ArchiveBoxIcon,
      url: process.env.REACT_APP_INSPECTIONS_URL && `${process.env.REACT_APP_INSPECTIONS_URL}/warehouse-safety/inspections`,
      group: 'Health & Safety Management',
      headerKey: 'healthSafetyManagement',
    },
    {
      name: 'Installation Live Audit Overhead (SHEQ Only)',
      description: 'View and manage installation live audit overhead records.',
      icon: BoltIcon,
      url: process.env.REACT_APP_INSPECTIONS_URL && `${process.env.REACT_APP_INSPECTIONS_URL}/installation-live-audit-overhead/inspections`,
      group: 'Health & Safety Management',
      headerKey: 'healthSafetyManagement',
    },
    {
      name: 'DSE Workstation Self-Assessment Checklist',
      description: 'View and manage dse workstation self-assessment checklist records.',
      icon: ComputerDesktopIcon,
      url: process.env.REACT_APP_INSPECTIONS_URL && `${process.env.REACT_APP_INSPECTIONS_URL}/dse-workstation/inspections`,
      group: 'Health & Safety Management',
      headerKey: 'healthSafetyManagement',
    },
    {
      name: 'Installation Live Audit Underground (SHEQ Only)',
      description: 'View and manage installation live audit underground records.',
      icon: CubeTransparentIcon,
      url: process.env.REACT_APP_INSPECTIONS_URL && `${process.env.REACT_APP_INSPECTIONS_URL}/installation-live-audit-underground/inspections`,
      group: 'Health & Safety Management',
      headerKey: 'healthSafetyManagement',
    },
    {
      name: 'Equipment Register',
      description: 'Every ladder and harness — where it is, and what its last inspection said.',
      icon: ArchiveBoxIcon,
      url: process.env.REACT_APP_INSPECTIONS_URL && `${process.env.REACT_APP_INSPECTIONS_URL}/equipment-register`,
      group: 'Health & Safety Administration',
      headerKey: 'healthSafetyAdministration',
    },
    {
      name: 'Re-Inspections',
      description: 'Equipment due for re-inspection, grouped by how soon.',
      icon: CalendarDaysIcon,
      url: process.env.REACT_APP_INSPECTIONS_URL && `${process.env.REACT_APP_INSPECTIONS_URL}/re-inspections`,
      group: 'Health & Safety Administration',
      headerKey: 'healthSafetyAdministration',
    },
    {
      name: 'Equipment Compliance Dashboard',
      description: 'Historic pass/fail trends for Ladder and Harness inspections.',
      icon: ChartBarIcon,
      url: process.env.REACT_APP_INSPECTIONS_URL && `${process.env.REACT_APP_INSPECTIONS_URL}/equipment-dashboard`,
      group: 'Health & Safety Administration',
      headerKey: 'healthSafetyAdministration',
    },
    {
      name: 'Audit Activity Dashboard',
      description: 'Historic pass/fail trends for Pre-Climb and Safe Drilling audits.',
      icon: ClipboardDocumentCheckIcon,
      url: process.env.REACT_APP_INSPECTIONS_URL && `${process.env.REACT_APP_INSPECTIONS_URL}/audit-dashboard`,
      group: 'Health & Safety Administration',
      headerKey: 'healthSafetyAdministration',
    },
    {
      name: 'Track Changes',
      description: 'Every amendment made to an inspection or audit, for ISO review.',
      icon: ClockIcon,
      url: process.env.REACT_APP_INSPECTIONS_URL && `${process.env.REACT_APP_INSPECTIONS_URL}/track-changes`,
      group: 'Health & Safety Administration',
      headerKey: 'healthSafetyAdministration',
    },
  ],
  communications: [
    {
      name: 'Comms Dispatch',
      description: 'Build and send messages to individuals, a Region/ISP/Cluster, a group, or everyone — with images and video.',
      icon: PaperAirplaneIcon,
      // Internal dashboard route; the tech API gates it on NEO2-Communications-Access.
      url: '/comms',
    },
    {
      name: 'Message Analytics',
      description: 'Read and acknowledgement analytics for every message sent — per-message dashboard, read-over-time, and CSV/PDF export.',
      icon: ChartBarIcon,
      url: '/comms/analytics',
    },
    {
      name: 'Nano Quest',
      description: 'Run Nano Quest: write and publish Quests (quizzes, Spot the Risk scenes), review Values in Action nominations, adjust points, record prizes and export reports.',
      icon: TrophyIcon,
      // Internal dashboard route; the tech API gates it on NEO2-Communications-Access.
      url: '/quest',
    },
    {
      name: 'Recipient Activity',
      description: 'Per-recipient engagement — pick a person to see their full history of reads and acknowledgements across every message sent to them.',
      icon: UserGroupIcon,
      url: '/comms/recipients',
    },
  ],
  support: [
    {
      name: 'Support Console',
      description: 'Live assistance for engineers — reply to help requests from the field in a chat thread; the engineer is notified in their app.',
      icon: LifebuoyIcon,
      // Internal dashboard route; the tech API gates it on Nano-Support-Access.
      url: '/support',
    },
    {
      name: 'AI Assistant Review',
      description: 'What engineers ask the AI assistant, what it could not answer, answers flagged as wrong, and answers to re-check after a procedure changes.',
      icon: SparklesIcon,
      // Internal dashboard route; the dashboard server gates it on Nano-Support-Access.
      url: '/support/assistant',
    },
  ],
  operations: [
    {
      name: 'Job Cards',
      description: 'Scheduler board — all install job cards for the day, live from SharePoint.',
      icon: CalendarDaysIcon,
      url: '/job-cards',
    },
    {
      name: 'Engineer Directory',
      description: 'Source of truth for engineers — Region/ISP/Cluster/Banding/Reports-To, synced nightly from the Ops Match Us list.',
      icon: UserGroupIcon,
      url: process.env.REACT_APP_ENGINEERS_URL,
    },
    {
      name: 'General SSRA Archive',
      description: 'General (non-type-specific) Site Specific Risk Assessment records migrated from SharePoint.',
      icon: ArchiveBoxIcon,
      url: process.env.REACT_APP_SSRA_ARCHIVE_URL,
    },
    {
      name: 'Match-Ups',
      description: 'Editable engineer match-up list — region, daily target, on-capacity, ISP/cluster. The native source FDC reads (parallel-run with SharePoint).',
      icon: ClipboardDocumentCheckIcon,
      url: '/operations/match-ups',
    },
    {
      name: 'FDC — Capacity Planning',
      description: 'Field/Force Daily Capacity: engineer availability per region per day, with annual leave, weekend cover and temporary region moves.',
      icon: CalendarDaysIcon,
      url: '/operations/fdc',
    },
    {
      // ODIN bookings against FDC capacity per region (20/10/2026). Same
      // Operations app and gate as FDC.
      name: 'ODIN Calendar',
      description: 'What ODIN has booked per region and day, set against FDC capacity. Link ODIN planners to regions on its mapping page.',
      icon: CalendarDaysIcon,
      url: '/operations/odin-calendar',
    },
    {
      name: 'Pole Survey',
      description: 'Openreach inspection record for a pole \u2014 climb status, hoist need, CBT and drop distances \u2014 before a job is sent.',
      icon: MapPinIcon,
      url: '/pole-survey',
      // Own header (19/10/2026): the map, its log and Climb Check together.
      // No headerKey here: the map stays open to all of Operations.
      group: 'Pole Surveys',
    },
    {
      // Pole survey audit log (TKT-00042): who surveyed which address, the
      // outcome and how long it took. Only Nano-Pole-Survey-Audit-Access.
      name: 'Survey Log',
      description: 'Every pole survey: who surveyed which address, the outcome, how long it took and what they checked.',
      icon: ClipboardDocumentCheckIcon,
      url: '/pole-survey/log',
      group: 'Pole Surveys',
      headerKey: 'poleSurveyAudit',
    },
    {
      // Climb Check: tomorrow's ODIN jobs surveyed for climb / hoist / route
      // via another pole. Pole survey managers only for now.
      name: 'Climb Check',
      description: 'Tomorrow’s jobs: climbable, hoist, or run the drop via another pole.',
      icon: ClipboardDocumentCheckIcon,
      url: '/climb-check',
      group: 'Pole Surveys',
      headerKey: 'poleSurveyAudit',
    },
  ],
  logistics: [
    {
      name: 'Stock Pulse',
      description: 'Ordered against actually used — per item, per engineer, per van.',
      icon: ScaleIcon,
      url: '/stock-pulse',
      group: 'Stock Management',
      headerKey: 'inventory-ops',
    },
    {
      name: 'Stock Transfers',
      description: 'Stock handed between engineers’ vans — who gave what to whom, and what is still waiting to be confirmed.',
      icon: ArrowsRightLeftIcon,
      url: '/van-transfers',
      group: 'Stock Management',
      headerKey: 'inventory-ops',
    },
    {
      name: 'Stock Disputes',
      description: 'Stock logged off another engineer’s van, where the owner says it was not his.',
      icon: ScaleIcon,
      url: '/stock-disputes',
      group: 'Stock Management',
      headerKey: 'inventory-ops',
    },
    {
      name: 'Job Card Stock',
      description: 'What engineers logged against their job cards — ISP and consumables, by item or by job.',
      icon: ClipboardDocumentListIcon,
      url: '/job-card-stock',
      group: 'Stock Management',
      headerKey: 'inventory-ops',
    },
    {
      name: 'Job Card Stock Form Order',
      description: 'Arrange the order engineers see when logging stock against a job card.',
      icon: Bars3BottomLeftIcon,
      url: '/job-card-stock-order',
      group: 'Stock Management',
      headerKey: 'inventory-ops',
    },
    {
      name: 'Van Stock',
      description: 'ISP stock per van — latest weekly count minus job-card usage.',
      icon: TruckIcon,
      url: '/van-stock',
      group: 'Stock Management',
      headerKey: 'inventory-ops',
    },
    {
      name: 'Stock Procurement',
      description: 'Raise a stock order from the master catalog — sent to the Head of Logistics for approval.',
      icon: ShoppingCartIcon,
      url: '/procurement',
      group: 'Stock Procurement',
      headerKey: 'procurement',
    },
    {
      name: 'Procurement Approvals',
      description: 'Final-approve stock orders; assign suppliers and generate the purchase orders.',
      icon: ClipboardDocumentCheckIcon,
      url: '/procurement/approvals',
      group: 'Stock Procurement',
      headerKey: 'procurement',
    },
    {
      name: 'SKU Catalog',
      description: 'The master product list — prices, suppliers and Xero sync.',
      icon: CubeIcon,
      url: '/platform/products',
      group: 'Stock Procurement',
      headerKey: 'procurement',
    },
    {
      name: 'Goods Receiving',
      description: 'Book in deliveries against purchase orders and bring stock into inventory.',
      icon: InboxArrowDownIcon,
      url: '/procurement/receiving',
      group: 'Stock Procurement',
      headerKey: 'procurement',
    },
    {
      name: 'Back Orders',
      description: 'Ordered and not yet accepted — including anything that arrived damaged or was rejected.',
      icon: ClockIcon,
      url: '/procurement/back-orders',
      group: 'Stock Procurement',
      headerKey: 'procurement',
    },
    {
      name: 'Stock on Hand',
      description: 'Live inventory per storeroom, driven by goods receiving.',
      icon: ArchiveBoxIcon,
      url: '/procurement/inventory',
      group: 'Stock Procurement',
      headerKey: 'procurement',
    },
    {
      name: 'Submitted Stock Counts',
      description: 'View and manage submitted ISP stock counts across both regions.',
      icon: CubeIcon,
      url: process.env.REACT_APP_STOCK_COUNTS_URL,
      group: 'Weekly ISP Count',
      // Matches nano-fibre-logistics' own RequireGroup gate on this page
      // (headerKey="inventory-ops") — only shown here if the user could
      // actually get past that gate, not just because the department is
      // visible to them.
      headerKey: 'inventory-ops',
    },
    {
      // Singular: the two per-region forms were merged into one combined form
      // (2026-07-21), where regions are a selection rather than separate
      // pages.
      name: 'ISP Stock Count Form',
      description: 'Submit a stock count for Region N, Region B or Remediation Stock.',
      icon: CubeIcon,
      // Deep-links to the form itself. It previously pointed at the logistics
      // root, which landed on that app's home screen and left the user to
      // find the form — the card names a form, so it should open one.
      url: process.env.REACT_APP_LOGISTICS_URL && `${process.env.REACT_APP_LOGISTICS_URL}/stock-count`,
      group: 'Weekly ISP Count',
      // Nav-visibility only (2026-07-14) — the underlying page in
      // nano-fibre-logistics still has no RequireGroup gate (any
      // department member, or NEO2-FSM-Stock-Access via FSM, can submit a
      // count regardless of this header). This just hides the dashboard
      // sidebar link from Logistics members who aren't in Inventory &
      // Operations, since field engineers reach the forms via FSM, not
      // this dashboard.
      headerKey: 'inventory-ops',
    },
    {
      name: 'ISP SKU Manager',
      description: 'View, add, and manage the ISP stock item catalog.',
      icon: CubeIcon,
      url: process.env.REACT_APP_SKU_MANAGER_URL,
      group: 'Weekly ISP Count',
      headerKey: 'inventory-ops',
    },
    {
      name: 'Storeroom Count Form',
      description: 'Submit the monthly storeroom count — Consumables, Tools, Uniform & PPE and Civil.',
      icon: CubeIcon,
      url: process.env.REACT_APP_LOGISTICS_URL && `${process.env.REACT_APP_LOGISTICS_URL}/storeroom-count`,
      group: 'Monthly Storeroom Count',
      // Nav-visibility only — the storeroom count form itself is open to any
      // Logistics user (same as the ISP count). This just hides the dashboard
      // link from Logistics members who aren't in Inventory & Operations.
      headerKey: 'inventory-ops',
    },
    {
      name: 'Storeroom Counts',
      description: 'View submitted monthly storeroom counts, with per-list and combined CSV export.',
      icon: CubeIcon,
      url: process.env.REACT_APP_LOGISTICS_URL && `${process.env.REACT_APP_LOGISTICS_URL}/storeroom-counts`,
      group: 'Monthly Storeroom Count',
      headerKey: 'inventory-ops',
    },
    {
      name: 'Storeroom SKU Manager',
      description: 'Manage the storeroom item catalog across all four lists — active/on-form toggles, ordering and prices.',
      icon: CubeIcon,
      url: process.env.REACT_APP_LOGISTICS_URL && `${process.env.REACT_APP_LOGISTICS_URL}/storeroom-sku-manager`,
      group: 'Monthly Storeroom Count',
      headerKey: 'inventory-ops',
    },
    {
      name: 'SIM Manager',
      description: "Assign and change company mobile numbers. Feeds the contact number shown in the company directory.",
      icon: DevicePhoneMobileIcon,
      // An internal dashboard route, not another module's URL — hence a
      // literal path rather than an env var. It renders as a normal link, so
      // it needs no special handling in ModulePanel.
      url: '/sims',
      // Grouped with the other stock/asset tools. Deliberately no headerKey:
      // the API gates this on the Logistics department, so gating the card on
      // inventory-ops as its siblings do would hide it from Logistics members
      // who can in fact use it.
      group: 'Asset Management',
      headerKey: 'assetManagement',
    },
    {
      name: 'Device Register',
      description: 'Company laptops and desktops — provisioning checklist, who holds what, and returns. The ISO 27001 asset-provisioning evidence.',
      icon: ComputerDesktopIcon,
      // Internal dashboard route (renders as a normal link). Like SIM Manager,
      // the tech API gates this on the Logistics department, so deliberately no
      // headerKey — that would hide it from Logistics members who can use it.
      url: '/devices',
      group: 'Asset Management',
      headerKey: 'assetManagement',
    },
    {
      name: 'Devices to Collect',
      description: 'Company laptops and desktops still held by people being offboarded — mark them collected as the kit comes back.',
      icon: InboxArrowDownIcon,
      // Logistics collection queue. Server-gated on the Logistics department
      // (full-admin bypass), same as the Device Register — so no headerKey.
      url: '/devices/to-collect',
      group: 'Asset Management',
      headerKey: 'assetManagement',
    },
  ],
  // Field engineers whose ONLY department is this one never see this card at
  // all — AuthenticatedLayout.jsx redirects them straight to FSM before the
  // dashboard shell renders. This entry exists for office staff who also have
  // FSM alongside another department.
  fsm: [
    {
      name: 'Open FSM',
      description: 'Field Service Mobile — the engineer-facing portal.',
      icon: DevicePhoneMobileIcon,
      url: process.env.REACT_APP_FSM_URL,
    },
  ],
  // Course-*taking* ("My Learning") is deliberately NOT here — it's an
  // ALWAYS_VISIBLE_LINKS entry in the manifest, since it applies to every
  // employee, not just Training dept members. These cards are admin-only.
  training: [
    {
      name: 'Training Matrix',
      description: 'Staff accreditations, expiry dates, and certificates — synced from monday.',
      icon: ClipboardDocumentCheckIcon,
      url: process.env.REACT_APP_TRAINING_MATRIX_URL || null,
      headerKey: 'trainingMatrix',
      group: 'Training Records',
    },
    {
      name: 'Course Builder',
      description: 'Author courses, assign them to staff, and review results.',
      icon: WrenchScrewdriverIcon,
      url: process.env.REACT_APP_TRAINING_URL ? `${process.env.REACT_APP_TRAINING_URL}/course-builder` : null,
      // Course Administration members see this too, without needing
      // separate direct membership — see permissionsService.js's
      // getMyAccess, which adds 'courseManagement' automatically for
      // courseAdmin members.
      headerKey: 'courseManagement',
      group: 'Course Management',
    },
    {
      name: 'Dispatch Manager',
      description: 'Assign courses to people, bulk-dispatch, and send reminders.',
      icon: PaperAirplaneIcon,
      url: process.env.REACT_APP_TRAINING_URL ? `${process.env.REACT_APP_TRAINING_URL}/dispatch` : null,
      // Narrower than courseManagement, same header as Course Insights/
      // History below — see permissionGroups.js's headers[] entry and
      // nano-fibre-training's own dispatch/analytics routes for why.
      headerKey: 'courseAdmin',
      group: 'Course Administration',
    },
    {
      name: 'Course Insights',
      description: 'Per-course completion/fail stats — who still needs to finish, who has.',
      icon: ChartBarIcon,
      url: process.env.REACT_APP_TRAINING_URL ? `${process.env.REACT_APP_TRAINING_URL}/insights` : null,
      headerKey: 'courseAdmin',
      group: 'Course Administration',
    },
    {
      name: 'Course History',
      description: 'Who changed what, and when — a per-course audit trail.',
      icon: ClockIcon,
      url: process.env.REACT_APP_TRAINING_URL ? `${process.env.REACT_APP_TRAINING_URL}/audit-log` : null,
      headerKey: 'courseAdmin',
      group: 'Course Administration',
    },
  ],
  fleet: [
    {
      name: 'OOH Approvals',
      description: 'Out-of-hours vehicle usage requests — review and approve.',
      icon: TruckIcon,
      url: process.env.REACT_APP_FLEET_URL ? `${process.env.REACT_APP_FLEET_URL}/ooh/fleet-queue` : null,
      headerKey: 'fleetManagement',
      group: 'Fleet Management',
    },
  ],
};

// Items whose URL env var is unset are dropped — that is how a module absent
// from an environment disappears instead of linking somewhere that isn't
// there. A department left with nothing then disappears too: listing it would
// open an empty panel, which reads as a broken page rather than as "not
// deployed here". Health & Safety is entirely inspections-backed, so on
// production (no inspections deployment) it drops out completely.
export const DEPARTMENTS = DEPARTMENT_SPINE
  .map((dept) => ({ ...dept, items: (DEPARTMENT_ITEMS[dept.key] || []).filter((item) => item.url) }))
  .filter((dept) => dept.items.length > 0);

// `open`/`onClose` drive the mobile off-canvas drawer. Below lg the sidebar is
// a fixed slide-in panel over a backdrop; at lg+ it's a normal static column
// (always visible, translate/backdrop no-op).
function Sidebar({ selected, onSelect, departments, isLoading, open = false, onClose = () => {}, showPlatform = false }) {
  const { instance } = useMsal();

  // React Router's location, NOT window.location: the latter is read during
  // render and never tells React it changed, so a client-side navigation can
  // leave the previous item highlighted. One value, shared by both lists.
  const { pathname } = useLocation();

  // Unread comms count, for the "My Messages" badge.
  const { data: unreadMessages } = useInboxUnread();

  const handleLogout = () => {
    instance.logoutRedirect();
  };

  return (
    <>
      {open && (
        <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={onClose} aria-hidden="true" />
      )}
      {/* h-dvh, NOT h-screen: 100vh is the LARGE mobile viewport (URL bar
          hidden), so the pinned Logout footer sat underneath the browser
          address bar and was unreachable. 100dvh tracks the visible
          viewport instead. inset-y-0 is the fallback if dvh is unsupported. */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 flex h-dvh w-72 shrink-0 flex-col border-r border-gray-200 bg-white transition-transform duration-200 lg:static lg:z-auto lg:translate-x-0 ${
          open ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
      <div className="relative px-4 py-6 text-center">
        <img src={`${process.env.PUBLIC_URL}/nano-fibre-logo.svg`} alt="Nano Fibre UK" className="mx-auto mb-2 h-auto w-3/5" />
        <p className="text-xl font-bold text-gray-900">Nano</p>
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 rounded-md p-1 text-gray-400 hover:bg-gray-100 lg:hidden"
          aria-label="Close menu"
        >
          <XMarkIcon className="h-6 w-6" />
        </button>
      </div>

      <nav className="flex-1 space-y-1 overflow-y-auto p-3">
        {PLACEHOLDER_ITEMS.map((item) => {
          const Icon = item.icon;
          return (
            <div
              key={item.name}
              className="flex w-full items-center gap-3 rounded-md px-4 py-3 text-base font-medium text-gray-400"
            >
              <Icon className="h-7 w-7 text-gray-300" />
              {item.name}
            </div>
          );
        })}

        {ALWAYS_VISIBLE_LINKS.map((item) => {
          const Icon = item.icon;
          return (
            <a
              key={item.name}
              href={item.url}
              className="flex w-full items-center gap-3 rounded-md px-4 py-3 text-base font-medium text-gray-700 hover:bg-brand-light"
            >
              <Icon className="h-7 w-7 text-brand" />
              {item.name}
            </a>
          );
        })}

        {ALWAYS_VISIBLE_ROUTES.map((item) => {
          const Icon = item.icon;
          const isActive = pathname === item.path;
          const badge = item.path === '/messages' && unreadMessages > 0 ? unreadMessages : null;
          return (
            <a
              key={item.name}
              href={item.path}
              onClick={onClose}
              className={`flex w-full items-center gap-3 rounded-md px-4 py-3 text-base font-medium ${
                isActive ? 'bg-brand-light text-brand-dark' : 'text-gray-700 hover:bg-brand-light'
              }`}
            >
              <span className="relative">
                <Icon className="h-7 w-7 text-brand" />
                {badge && (
                  <span className="absolute -right-2 -top-1.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">{badge}</span>
                )}
              </span>
              {item.name}
            </a>
          );
        })}

        <div className="mx-4 my-2 h-px bg-gray-200" />

        {isLoading && <p className="px-4 py-2 text-sm text-gray-400">Loading access...</p>}
        {departments.map((dept) => {
          const Icon = dept.icon;
          const isSelected = selected === dept.key;
          return (
            <button
              key={dept.key}
              type="button"
              onClick={() => {
                onSelect(isSelected ? null : dept.key);
                onClose();
              }}
              className={`flex w-full items-center gap-3 rounded-md px-4 py-3 text-base font-medium ${
                isSelected ? 'bg-brand-light text-brand-dark' : 'text-gray-700 hover:bg-brand-light'
              }`}
            >
              <Icon className="h-7 w-7 text-brand" />
              {dept.name}
            </button>
          );
        })}

        {/* Platform — full admins only, and visually separated from the
            departments above because it isn't one. */}
        {showPlatform ? (
          <>
            <div className="mx-4 my-2 h-px bg-gray-200" />
            <p className="px-4 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-gray-400">Platform</p>
            {PLATFORM_ITEMS.map((item) => {
              const Icon = item.icon;
              const isActive = pathname === item.path;
              return (
                <a
                  key={item.name}
                  href={item.path}
                  onClick={onClose}
                  className={`flex w-full items-center gap-3 rounded-md px-4 py-3 text-base font-medium ${
                    isActive ? 'bg-brand-light text-brand-dark' : 'text-gray-700 hover:bg-brand-light'
                  }`}
                >
                  <Icon className="h-7 w-7 text-brand" />
                  {item.name}
                </a>
              );
            })}
          </>
        ) : null}
      </nav>

      <div className="border-t border-gray-200 p-3">
        <button
          type="button"
          onClick={handleLogout}
          className="flex w-full items-center gap-3 rounded-md px-4 py-3 text-base font-medium text-gray-700 hover:bg-brand-light"
        >
          <ArrowLeftStartOnRectangleIcon className="h-7 w-7 text-brand" />
          Logout
        </button>
      </div>
      </aside>
    </>
  );
}

export default Sidebar;
